import {
	consoleLoggingIntegration,
	feedbackIntegration,
	init,
	reactErrorHandler,
	replayIntegration,
	showReportDialog,
	tanstackRouterBrowserTracingIntegration,
} from "@sentry/react";

export const SENTRY_ENVIRONMENT =
	import.meta.env.VITE_SENTRY_ENVIRONMENT || "local";
export const SENTRY_ENABLED = SENTRY_ENVIRONMENT !== "local";

export type SentryConfig = {
	dsn: string;
	tunnel?: string;
};

// biome-ignore lint/suspicious/noExplicitAny: router instance type varies
export function initSentry(router: any, config: SentryConfig) {
	if (!SENTRY_ENABLED) return;

	init({
		dsn: config.dsn,
		sendDefaultPii: true,
		tunnel: config.tunnel,
		environment: SENTRY_ENVIRONMENT,
		tracesSampleRate: 1.0,
		ignoreErrors: [
			// ELASO-1H / ELASO-HR: Browser cancels fetch on navigation/tab close or media stream abort
			"AbortError",
			// ELASO-8E: iOS Safari kills IndexedDB when recycling background tabs
			"IDBDatabase",
			"IndexedDB",
			// ELASO-G: User denying camera/mic/screen share permission prompt
			"NotAllowedError",
			"Permission denied",
			// ELASO-HV: getDisplayMedia not supported on mobile/older browsers
			"DeviceUnsupportedError",
			"getDisplayMedia not supported",
		],
		enableLogs: true,
		integrations: [
			tanstackRouterBrowserTracingIntegration(router),
			replayIntegration(),
			feedbackIntegration({
				colorScheme: "system",
				autoInject: false,
				enableScreenshot: true,
			}),
			consoleLoggingIntegration({ levels: ["warn", "error"] }),
		],
		replaysSessionSampleRate: 1.0,
		replaysOnErrorSampleRate: 1.0,
		beforeSend(event) {
			const dominated = event.exception?.values?.some(
				(ex) => ex.mechanism?.handled === false
			);
			if (dominated && event.event_id) {
				showReportDialog({ eventId: event.event_id });
			}
			return event;
		},
	});
}

export { logger as sentryLogger } from "@sentry/react";

export const sentryCreateRootOptions = SENTRY_ENABLED
	? {
			onUncaughtError: reactErrorHandler(),
			onCaughtError: reactErrorHandler(),
			onRecoverableError: reactErrorHandler(),
		}
	: {};
